import type { HexMapState } from '../../engine/render/renderer';
import { getFoodPerPersonPerHour, getPopulationCount } from '../../engine/map/settlement';
import { getTreeCutWork } from '../../engine/forest/treeMath';
import { t } from '../../i18n/translations';

export type SettlementView = HexMapState['settlementsById'][string];
export type AgeBar = { label: string; value: number; max: number; tone: 'good' | 'warn' };

export function getTreeTotal(settlement: SettlementView): number {
    return settlement.regionResources.trees.reduce((sum, entry) => sum + entry.amount, 0);
}

export function getWildlifeTotal(settlement: SettlementView): number {
    return settlement.regionResources.wild_life.reduce((sum, entry) => sum + entry.amount, 0);
}

export function getHuntTicks(state: HexMapState, settlement: SettlementView): number {
    const huntableAgeMin = state.config.wildLife.huntableAgeMin;
    const huntable = settlement.regionResources.wild_life
        .filter((entry) => entry.age >= huntableAgeMin)
        .reduce((sum, entry) => sum + entry.amount, 0);
    if (huntable <= 0) return state.config.production.hunter.minHuntTicks;
    const ratio = state.config.production.hunter.ticksPerHuntRatio;
    const ticks = ratio.ticks * (ratio.wildLife / huntable);
    return Math.max(state.config.production.hunter.minHuntTicks, Math.round(ticks * 10) / 10);
}

export function getLumberCampTarget(
    state: HexMapState,
    settlement: SettlementView,
): { age: number; ticks: number } | null {
    if (settlement.regionResources.trees.length === 0) return null;
    const nextTree = settlement.regionResources.trees
        .filter((entry) => entry.amount > 0)
        .sort((a, b) => b.age - a.age)[0];
    if (!nextTree) return null;
    const ticks = getTreeCutWork(state.config, nextTree.age);
    return { age: nextTree.age, ticks };
}

export function getFoodHoursSupply(
    settlement: SettlementView,
    config: HexMapState['config'],
): number {
    const population = getPopulationCount(settlement);
    if (population <= 0) return 0;
    const totalFood = settlement.houses.reduce((sum, house) => sum + house.storage.food, 0);
    const foodPerHour = getFoodPerPersonPerHour(config);
    const hours = foodPerHour > 0 ? totalFood / (population * foodPerHour) : 0;
    return Math.round(hours * 10) / 10;
}

function resourceLabel(lang: HexMapState['config']['ui']['language'], key: string): string {
    const label = t(lang, `resource.${key}`);
    return label === `resource.${key}` ? key : label;
}

export function formatCostLocalized(
    lang: HexMapState['config']['ui']['language'],
    cost: Record<string, number>,
): string {
    return Object.entries(cost)
        .map(([key, amount]) => `${resourceLabel(lang, key)} ${amount}`)
        .join(', ');
}

export function getWarnings(
    lang: HexMapState['config']['ui']['language'],
    settlement: SettlementView,
): string[] {
    const warnings: string[] = [];
    const farmerMissing = Math.max(0, settlement.jobs.farmers - settlement.tools.farmers.length);
    const hunterMissing = Math.max(0, settlement.jobs.hunters - settlement.tools.hunters.length);
    if (farmerMissing > 0) warnings.push(t(lang, 'log.farmersNoTools', farmerMissing));
    if (hunterMissing > 0) warnings.push(t(lang, 'log.huntersNoTools', hunterMissing));
    return warnings;
}

function buildAgeBars(
    lang: HexMapState['config']['ui']['language'],
    entries: { age: number; amount: number }[],
    isOptimal: (age: number) => boolean,
): AgeBar[] {
    if (entries.length === 0) return [];
    const max = entries.reduce((acc, entry) => Math.max(acc, entry.amount), 0);
    return entries
        .slice()
        .sort((a, b) => b.age - a.age)
        .map((entry) => ({
            label: t(lang, 'age.label', entry.age),
            value: entry.amount,
            max,
            tone: isOptimal(entry.age) ? 'good' : 'warn',
        }));
}

export function getTreeAgeBars(state: HexMapState, settlement: SettlementView): AgeBar[] {
    const peakAge = state.config.tree.peakAge;
    return buildAgeBars(
        state.config.ui.language,
        settlement.regionResources.trees,
        (age) => age >= peakAge,
    );
}

export function getWildlifeAgeBars(state: HexMapState, settlement: SettlementView): AgeBar[] {
    const minAge = state.config.wildLife.huntableAgeMin;
    return buildAgeBars(
        state.config.ui.language,
        settlement.regionResources.wild_life,
        (age) => age >= minAge,
    );
}
